import api from '../../../../api/apiService';
import { ENDPOINTS } from '../../../../api/endpoints';
import { ENV } from '../../../../config/env';

import {
  AddNtIdRequest,
  DropdownOption,
  NtIdEmployee,
  NtIdManagementRequest,
  NtIdManagementResponse,
  UpdateNtIdRequest,
  EmployeeDetailsResponse,
  DeleteNtIdRequest,
} from '../types/ntIdManagement.types';

import {
  mockNtIdManagementResponse,
  mockReportingManagers,
  mockRoles,
  mockShifts,
  mockStatuses,
  mockTeams,
} from '../Mock/ntIdManagement.mock';

// ==========================================================
// API RESPONSE TYPES
// ==========================================================

interface EmployeeNetworkMappingApiItem {
  employeeNetworkMappingId?: number;
  employeeCode?: string | null;
  networkId?: string | null;
  isActive?: boolean | null;
  fullName?: string | null;
  employeeName?: string | null;
  email?: string | null;
  team?: string | null;
  department?: string | null;
  managerName?: string | null;
  shiftId?: number | null;
  shiftName?: string | null;
  roleName?: string | null;
  roleId?: number | null;
}

interface EmployeeNetworkMappingApiPage {
  data?: EmployeeNetworkMappingApiItem[];
  pageNumber?: number;
  pageSize?: number;
  totalRecords?: number;
  totalCount?: number;
  totalPages?: number;
}

type EmployeeNetworkMappingApiResponse =
  | EmployeeNetworkMappingApiItem[]
  | EmployeeNetworkMappingApiPage;

// ==========================================================
// MAPPING
// ==========================================================

const mapEmployee = (item: EmployeeNetworkMappingApiItem): NtIdEmployee => ({
  employeeNetworkMappingId: item.employeeNetworkMappingId ?? 0,
  employeeCode: item.employeeCode ?? '',
  employeeName: item.fullName ?? item.employeeName ?? '',
  email: item.email ?? '',
  ntIds: item.networkId ? [item.networkId] : [],
  role: item.roleName ?? '',
  team: item.team ?? item.department ?? '',
  reportingManager: item.managerName ?? '',
  shift: item.shiftName ?? '',
  status: item.isActive === true ? 'Active' : 'Inactive',
});

const matchesSearch = (employee: NtIdEmployee, search: string): boolean => {
  const keyword = search.trim().toLowerCase();

  if (!keyword) return true;

  return (
    employee.employeeCode.toLowerCase().includes(keyword) ||
    employee.employeeName.toLowerCase().includes(keyword) ||
    employee.email.toLowerCase().includes(keyword) ||
    employee.reportingManager.toLowerCase().includes(keyword) ||
    employee.team.toLowerCase().includes(keyword) ||
    employee.role.toLowerCase().includes(keyword) ||
    employee.ntIds.some((ntId) => ntId.toLowerCase().includes(keyword))
  );
};

const paginateEmployees = (
  employees: NtIdEmployee[],
  pageNumber: number,
  pageSize: number,
): NtIdManagementResponse => {
  const startIndex = (pageNumber - 1) * pageSize;

  return {
    employees: employees.slice(startIndex, startIndex + pageSize),
    totalCount: employees.length,
    pageNumber,
    pageSize,
  };
};

// ==========================================================
// EMPLOYEE LIST - SEARCH AND PAGINATION
// ==========================================================

export const getNtIdEmployees = async (
  request: NtIdManagementRequest = {},
): Promise<NtIdManagementResponse> => {
  const pageNumber = Math.max(1, Math.floor(request.pageNumber ?? 1));
  const pageSize = Math.max(1, Math.floor(request.pageSize ?? 10));
  const search = request.search?.trim() ?? '';

  if (ENV.USE_MOCK) {
    const allEmployees = mockNtIdManagementResponse.employees;
    const filteredEmployees = allEmployees.filter((employee) =>
      matchesSearch(employee, search),
    );

    return paginateEmployees(filteredEmployees, pageNumber, pageSize);
  }

  const response = await api.get<EmployeeNetworkMappingApiResponse>(
    ENDPOINTS.EMPLOYEE_NETWORK_MAPPING,
    {
      params: {
        pageNumber,
        pageSize,
        ...(search ? { search } : {}),
      },
    },
  );

  const body = response.data;

  // Some API environments return a raw array instead of a paginated object.
  // In that case, filter and paginate the array locally.
  if (Array.isArray(body)) {
    const mappedEmployees = body.map(mapEmployee);
    const filteredEmployees = mappedEmployees.filter((employee) =>
      matchesSearch(employee, search),
    );

    return paginateEmployees(filteredEmployees, pageNumber, pageSize);
  }

  // Standard paginated response: { data: [], totalRecords: number, ... }
  const records = Array.isArray(body.data) ? body.data : [];
  const employees = records.map(mapEmployee);

  return {
    employees,
    totalCount: body.totalRecords ?? body.totalCount ?? employees.length,
    pageNumber: body.pageNumber ?? pageNumber,
    pageSize: body.pageSize ?? pageSize,
  };
};

// ==========================================================
// ADD EMPLOYEE
// ==========================================================

export const addNtIdEmployee = async (
  payload: AddNtIdRequest,
): Promise<void> => {
  if (ENV.USE_MOCK) return;

  await api.post(ENDPOINTS.NTID_ADD_EMPLOYEE, payload);
};

// ==========================================================
// UPDATE EMPLOYEE
// ==========================================================

export const updateNtIdEmployee = async (
  payload: UpdateNtIdRequest,
): Promise<void> => {
  if (ENV.USE_MOCK) return;

  await api.put(ENDPOINTS.NTID_UPDATE_EMPLOYEE, payload);
};

// ==========================================================
// DELETE EMPLOYEE
// ==========================================================

export const deleteNtIdEmployee = async (
  payload: DeleteNtIdRequest,
): Promise<void> => {
  if (ENV.USE_MOCK) return;

  await api.delete(ENDPOINTS.NTID_DELETE_EMPLOYEE, {
    data: payload,
  });
};

// ==========================================================
// BULK IMPORT
// ==========================================================

export const bulkImportNtIds = async (file: File) => {
  if (ENV.USE_MOCK) {
    return { message: 'Bulk import completed successfully.' };
  }

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await api.post(ENDPOINTS.NTID_BULK_IMPORT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

// ==========================================================
// EXPORT CSV
// ==========================================================

export const exportNtIds = async (
  payload: NtIdManagementRequest,
): Promise<Blob> => {
  const { data } = await api.get(ENDPOINTS.NTID_EXPORT, {
    params: payload,
    responseType: 'blob',
  });

  return data;
};

// ==========================================================
// ROLES
// ==========================================================

export const getRoles = async (): Promise<DropdownOption[]> => {
  if (ENV.USE_MOCK) return mockRoles;

  const { data } = await api.get(ENDPOINTS.NTID_ROLES);

  return data.map((role: any) => ({
    id: role.masterRoleId,
    label: role.name,
    value: role.name,
  }));
};

// ==========================================================
// STATUSES
// ==========================================================

export const getStatuses = async (): Promise<DropdownOption[]> => {
  if (ENV.USE_MOCK) return mockStatuses;

  const { data } = await api.get<DropdownOption[]>(ENDPOINTS.NTID_STATUSES);
  return data;
};

// ==========================================================
// TEAMS
// ==========================================================

export const getTeams = async (): Promise<DropdownOption[]> => {
  if (ENV.USE_MOCK) return mockTeams;

  const { data } = await api.get<DropdownOption[]>(ENDPOINTS.NTID_TEAMS);
  return data;
};

// ==========================================================
// SHIFTS
// ==========================================================

export const getShifts = async (): Promise<DropdownOption[]> => {
  if (ENV.USE_MOCK) return mockShifts;

  const { data } = await api.get(ENDPOINTS.NTID_SHIFTS);

  return data
    .filter(
      (shift: any, index: number, self: any[]) =>
        index === self.findIndex((item) => item.shiftId === shift.shiftId),
    )
    .map((shift: any) => ({
      id: shift.shiftId,
      label: shift.shiftName,
      value: shift.shiftName,
    }));
};

// ==========================================================
// REPORTING MANAGERS
// ==========================================================

export const getReportingManagers = async (): Promise<DropdownOption[]> => {
  if (ENV.USE_MOCK) return mockReportingManagers;

  const { data } = await api.get<DropdownOption[]>(
    ENDPOINTS.NTID_REPORTING_MANAGERS,
  );

  return data;
};

// ==========================================================
// EMPLOYEE DETAILS
// ==========================================================

export const getEmployeeDetails = async (
  employeeCode: string,
): Promise<EmployeeDetailsResponse> => {
  const { data } = await api.get<EmployeeDetailsResponse>(
    ENDPOINTS.NTID_EMPLOYEE_DETAILS,
    {
      params: { employeeCode },
    },
  );

  return data;
};
